import React from 'react';
import {
  Box,
  ParagraphText,
  Tabs,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { SectionHeader } from '@leagueplatform/genesis-core-alpha';
import { Formatter, useIntl } from '@leagueplatform/locales';
import {
  PillTab,
  PillTabList,
  PillTabs,
  LoadingIndicator,
  ErrorState,
} from '@leagueplatform/web-common-components';
import {
  SHORT_DATE_AND_TIME_FORMAT,
  SHORT_DATE_FORMAT,
} from '@leagueplatform/web-common';
import { useWalletParams } from '#hooks/use-wallet-params.hook.ts';
import { ProviderServiceV2 } from '@leagueplatform/wallet-api';
import { usePlanProgress } from './hooks/use-plan-progress.hook';

import { MemberDropDown } from '../member-dropdown/member-dropdown.component';
import { useMemberDropdown } from './hooks/use-member-dropdown.hook';
import { BalanceCategories } from './components/balance-categories/balance-categories.component';
import { useWalletConfig } from '../../../../wallet.config';
import { NETWORK_PLAN_ID } from './types/plan-progress.types';

const ACCUMULATION_PERIOD_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  ...SHORT_DATE_FORMAT,
  timeZone: 'UTC',
};

export const PlanProgress = () => {
  const { formatDate, $tmap, formatMessage } = useIntl();
  const { userId, benefitId } = useWalletParams();

  const {
    plans,
    accumulationPeriod,
    lastUpdated,
    carrierName,
    onTabClickEvent: handleTabClickEvent,
    onAccordionToggleEvent: handleAccordionToggleEvent,
    isLoading,
    isError,
    refetch,
    isNoNetworkUser,
    error,
  } = usePlanProgress(userId, benefitId!, 2);

  const handleButtonClick = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const { showMemberSelect } = useMemberDropdown();

  const { enableNoNetworkLiveBalances } = useWalletConfig();

  const isNoTabPlan =
    (enableNoNetworkLiveBalances && plans.length === 1) || isNoNetworkUser;
  const isAccumulationPeriodVisible =
    !isLoading && !isError && accumulationPeriod;

  const getPlanIDMessage = $tmap<NETWORK_PLAN_ID>({
    [NETWORK_PLAN_ID.IN_NETWORK]: 'IN_NETWORK',
    [NETWORK_PLAN_ID.OUT_OF_NETWORK]: 'OUT_OF_NETWORK',
    [NETWORK_PLAN_ID.NO_NETWORK]: 'NETWORK_PLAN_ID',
  });

  const strong: Formatter = (msg) => <Box as="strong">{msg}</Box>;

  return (
    <StackLayout spacing="$oneAndHalf" as="section">
      {showMemberSelect && <MemberDropDown />}
      <StackLayout spacing="$one">
        <SectionHeader
          headingLevel="2"
          title={formatMessage({ id: 'PLAN_PROGRESS' })}
        />
        {isAccumulationPeriodVisible && (
          <ParagraphText size="sm">
            {formatMessage(
              { id: 'ACCUMULATION_PERIOD' },
              {
                start: formatDate(
                  accumulationPeriod.start,
                  ACCUMULATION_PERIOD_DATE_FORMAT,
                ),
                end: formatDate(
                  accumulationPeriod.end,
                  ACCUMULATION_PERIOD_DATE_FORMAT,
                ),
                strong,
              },
            )}
          </ParagraphText>
        )}
      </StackLayout>
      {isLoading && (
        <Box css={{ width: '100%' }}>
          <LoadingIndicator inline />
        </Box>
      )}
      {isError && !isLoading && (
        <Box
          as="section"
          css={{
            backgroundColor: '$surfaceBackgroundTertiary',
            padding: '$one',
            minHeight: '286px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <ErrorState
            hideImage
            onButtonClick={handleButtonClick}
            error={error}
          />
        </Box>
      )}
      {!isLoading && !isError && (
        <StackLayout css={{ width: '100%' }} spacing="$oneAndHalf">
          {!isNoTabPlan && (
            <PillTabs
              css={{ width: '100%' }}
              onValueChange={(value) => {
                handleTabClickEvent(
                  plans[plans.findIndex((plan) => plan.id === value)]
                    .analyticsId,
                );
              }}
              defaultValue={plans[0]?.id}
            >
              <StackLayout css={{ width: '100%' }} spacing="$oneAndHalf">
                <Box css={{ width: 'fit-content' }}>
                  <PillTabList>
                    {plans.map((tab) => (
                      <PillTab key={tab.id} value={tab.id}>
                        {getPlanIDMessage(tab.id)}
                      </PillTab>
                    ))}
                  </PillTabList>
                </Box>
                {plans.map((plan) => {
                  const LAST_UPDATED = formatMessage(
                    { id: 'LAST_UPDATED_CARRIER_DATE' },
                    {
                      date: formatDate(lastUpdated, SHORT_DATE_AND_TIME_FORMAT),
                      carrier: carrierName,
                    },
                  );
                  return (
                    <Tabs.TabPanel
                      css={{ padding: '$none', width: '100%' }}
                      key={plan.id}
                      value={plan.id}
                    >
                      {plan.network?.length === 0 && (
                        <Box css={{ display: 'flex' }} as="section">
                          <ParagraphText>
                            {formatMessage(
                              { id: 'NETWORK_MISSING_COVERAGE' },
                              { network: plan.networkName },
                            )}
                          </ParagraphText>
                        </Box>
                      )}

                      {plan?.network?.length > 0 && (
                        <StackLayout spacing="$oneAndHalf">
                          {plan.network.map(
                            (balanceGroup: ProviderServiceV2, i: number) => (
                              <BalanceCategories
                                // eslint-disable-next-line react/no-array-index-key
                                key={`balance-group-${plan.networkName}-${i}`}
                                title={balanceGroup.title}
                                description={balanceGroup.description}
                                categories={balanceGroup.categories}
                                lastUpdated={LAST_UPDATED}
                                networkName={plan.networkName}
                                onProviderToggle={handleAccordionToggleEvent}
                              />
                            ),
                          )}
                        </StackLayout>
                      )}
                    </Tabs.TabPanel>
                  );
                })}
              </StackLayout>
            </PillTabs>
          )}
          {isNoTabPlan &&
            plans.map((plan) => {
              const LAST_UPDATED = formatMessage(
                { id: 'LAST_UPDATED_CARRIER_DATE' },
                {
                  date: formatDate(lastUpdated, SHORT_DATE_AND_TIME_FORMAT),
                  carrier: carrierName,
                },
              );

              return plan.network.map(
                (balanceGroup: ProviderServiceV2, i: number) => (
                  <BalanceCategories
                    // eslint-disable-next-line react/no-array-index-key
                    key={`balance-group-${plan}-${i}`}
                    title={balanceGroup.title}
                    description={balanceGroup.description}
                    categories={balanceGroup.categories}
                    lastUpdated={LAST_UPDATED}
                    onProviderToggle={handleAccordionToggleEvent}
                    networkName={plan.networkName}
                  />
                ),
              );
            })}
        </StackLayout>
      )}
    </StackLayout>
  );
};
